import { useEffect, useRef, useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { Loader2 } from "lucide-react";
import api, { apiError } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";

/** Lands from the website admin with a one-time ticket and starts a session here. */
export default function SsoLanding() {
  const [params] = useSearchParams();
  const [error, setError] = useState("");
  const [manual, setManual] = useState("");
  const [busy, setBusy] = useState(false);
  const navigate = useNavigate();
  const { refresh } = useAuth();
  const done = useRef(false);

  const redeem = (ticket) => {
    setBusy(true);
    setError("");
    return api
      .post("/sso/redeem", { ticket })
      .then(async ({ data }) => {
        if (data.access_token) localStorage.setItem("asthetik_token", data.access_token);
        await refresh();
        navigate("/", { replace: true });
      })
      .catch((e) => setError(apiError(e)))
      .finally(() => setBusy(false));
  };

  useEffect(() => {
    if (done.current) return;
    done.current = true;
    const ticket = params.get("ticket");
    if (!ticket) return;
    // Tickets are single-use: never resend one that this tab already redeemed (reload, back button).
    if (sessionStorage.getItem("asthetik_sso_ticket") === ticket) {
      setError("That sign-in link was already used. Return to the website admin for a new one.");
      return;
    }
    sessionStorage.setItem("asthetik_sso_ticket", ticket);
    // Strip the ticket from the address bar before anything else can record it.
    window.history.replaceState({}, "", "/sso");
    redeem(ticket);
  }, [params]); // eslint-disable-line react-hooks/exhaustive-deps

  const submitManual = (e) => {
    e.preventDefault();
    const raw = manual.trim();
    const ticket = raw.includes("ticket=") ? raw.split("ticket=")[1].split("&")[0] : raw;
    if (ticket) redeem(decodeURIComponent(ticket));
  };

  const waiting = busy || (params.get("ticket") && !error);

  return (
    <div className="flex min-h-screen items-center justify-center px-6" data-testid="sso-landing">
      <div className="w-full max-w-md text-center">
        <div className="font-serif text-2xl tracking-tight text-ink">
          Ästhetik<span className="text-lime">.</span>
        </div>

        {waiting ? (
          <p className="mt-8 flex items-center justify-center gap-3 text-sm text-stone">
            <Loader2 className="h-4 w-4 animate-spin" strokeWidth={1.5} />
            Signing you in from asthetik.design…
          </p>
        ) : (
          <>
            {error && (
              <p data-testid="sso-error" className="mt-8 rounded-sm border border-[#E07A5F] bg-[#F6DED6] px-5 py-4 text-sm leading-relaxed text-[#A8492E]">
                {error}
              </p>
            )}
            <form onSubmit={submitManual} className="mt-8 text-left" data-testid="sso-manual-form">
              <label className="block">
                <span className="overline mb-2 block">Paste your sign-in link</span>
                <input
                  data-testid="sso-ticket-input"
                  value={manual}
                  onChange={(e) => setManual(e.target.value)}
                  placeholder="https://admin.asthetik.design/sso?ticket=…"
                  className="w-full rounded-sm border border-line bg-white px-4 py-3 text-sm outline-none transition-colors focus:border-lime"
                />
              </label>
              <p className="mt-3 text-xs leading-relaxed text-fog">
                From your website admin, until admin.asthetik.design points here. Links last 60 seconds.
              </p>
              <button
                data-testid="sso-redeem-btn"
                type="submit"
                disabled={!manual.trim() || busy}
                className="mt-5 w-full rounded-sm bg-lime px-6 py-3 text-sm font-medium text-ink transition-colors hover:bg-lime-dark disabled:opacity-60"
              >
                Sign in
              </button>
            </form>
            <button
              data-testid="sso-manual-login-btn"
              onClick={() => navigate("/login", { replace: true })}
              className="mt-6 text-xs uppercase tracking-[0.15em] text-stone underline-offset-4 hover:underline"
            >
              Sign in with a password instead
            </button>
          </>
        )}
      </div>
    </div>
  );
}
